import type { APILibraryCategoryResponseType } from "@/core/data_sources/models/APILibraryCategory";
import type { APILibraryTemplateResponseType } from "@/core/data_sources/models/APILibraryTemplate";
import type { APILibraryTemplatePackResponseType } from "@/core/data_sources/models/APILibraryTemplatePack";
import type { APILibraryVerticalResponseType } from "@/core/data_sources/models/APILibraryVertical";
import {
  AbortableRequest,
  SuccessResponse,
  type RequestResponse,
} from "@/core/utils/request";

import type { AxiosResponse } from "axios";

export type ReviewQueueStatus =
  | "Archived"
  | "Changes Requested"
  | "Pending Review"
  | "Rejected";

export type ReviewQueueItem = {
  readonly category: string;
  readonly changes?: string;
  readonly description: string;
  readonly fields: number;
  readonly id: string;
  readonly name: string;
  readonly source: "Imported Drafts" | "Tenant Submissions";
  readonly status: ReviewQueueStatus;
  readonly submittedBy: string;
  readonly submittedFrom: string;
  readonly type: "Form" | "Media" | "Model";
  readonly verticals: readonly string[];
  readonly version: string;
};

export type AccessPreset = {
  readonly capabilities?: readonly string[];
  readonly contexts?: readonly string[];
  readonly description?: string;
  readonly id: string;
  readonly name: string;
  readonly plans: readonly string[];
  readonly role: "Member" | "Owner" | "Supervisor" | "Viewer";
  readonly status: "Draft" | "Published";
};

export type AuditEntry = {
  readonly action: string;
  readonly reason: string;
  readonly target: string;
  readonly user: string;
  readonly when: string;
};

export const mockVerticals: APILibraryVerticalResponseType[] = [
  {
    id: 1,
    code: "GENERAL",
    name: "General",
    status: "active",
    is_system: true,
    created: "2026-01-05T10:00:00Z",
    modified: "2026-04-12T10:00:00Z",
  },
  {
    id: 2,
    code: "HVAC",
    name: "HVAC",
    status: "active",
    is_system: false,
    created: "2026-01-05T10:00:00Z",
    modified: "2026-04-10T10:00:00Z",
  },
  {
    id: 3,
    code: "SECURITY",
    name: "Security",
    status: "active",
    is_system: false,
    created: "2026-02-14T10:00:00Z",
    modified: "2026-04-03T10:00:00Z",
  },
  {
    id: 4,
    code: "FACILITY",
    name: "Facility",
    status: "active",
    is_system: false,
    created: "2026-02-14T10:00:00Z",
    modified: "2026-04-03T10:00:00Z",
  },
  {
    id: 5,
    code: "TELCO",
    name: "Telecom",
    status: "active",
    is_system: false,
    created: "2026-03-01T10:00:00Z",
    modified: "2026-03-20T10:00:00Z",
  },
];

export const mockCategories: APILibraryCategoryResponseType[] = [
  { id: 1, name: "Installation", is_system: true },
  { id: 2, name: "Maintenance", is_system: true },
  { id: 3, name: "Diagnostic", is_system: false },
  { id: 4, name: "Safety", is_system: false },
  { id: 5, name: "Inspection", is_system: true },
  { id: 6, name: "Customer Sign-off", is_system: false },
];

const verticalByName = (name: string) =>
  mockVerticals.find((vertical) => vertical.name === name) ?? mockVerticals[0];
const categoryByName = (name: string) =>
  mockCategories.find((category) => category.name === name) ??
  mockCategories[0];

export const mockTemplates: APILibraryTemplateResponseType[] = [
  {
    id: 101,
    name: "HVAC Installation Form",
    type: "form",
    verticals: [verticalByName("HVAC")],
    categories: [categoryByName("Installation")],
    source: "library",
    status: "published",
    version: 1,
    fields_count: 18,
    shots_count: null,
    packs: ["HVAC Starter Pack"],
    description: "Installation checklist for HVAC field jobs.",
    recommended: true,
    created: "2026-02-01T10:00:00Z",
    modified: "2026-03-28T10:00:00Z",
    payload_json: {
      sectionsData: [
        {
          name: "Section 1 - Site arrival",
          fields: [
            { icon: "TIME", name: "Arrival timestamp", required: true },
            { icon: "CAM", name: "Site entrance photo", required: true },
            { icon: "CHK", name: "Site access granted" },
          ],
        },
        {
          name: "Section 2 - Installation checks",
          fields: [
            { icon: "TXT", name: "Equipment model" },
            { icon: "CHK", name: "Cabling complete", required: true },
            { icon: "CHK", name: "Power on verified", required: true },
            { icon: "CAM", name: "Equipment label photo", required: true },
          ],
        },
      ],
    },
  },
  {
    id: 102,
    name: "Preventive Maintenance Form",
    type: "form",
    verticals: [verticalByName("HVAC"), verticalByName("Facility")],
    categories: [categoryByName("Maintenance")],
    source: "library",
    status: "published",
    version: 2,
    fields_count: 24,
    shots_count: null,
    packs: ["HVAC Starter Pack", "Facility Starter Pack"],
    description: "Preventive service checklist.",
    recommended: false,
    created: "2026-02-04T10:00:00Z",
    modified: "2026-04-02T10:00:00Z",
    payload_json: {
      sectionsData: [
        {
          name: "Section 1 - Equipment status",
          fields: [
            { icon: "TXT", name: "Equipment model", required: true },
            { icon: "TXT", name: "Serial number", required: true },
            { icon: "CHK", name: "Filters replaced" },
            { icon: "TXT", name: "Technician notes" },
          ],
        },
      ],
    },
  },
  {
    id: 103,
    name: "Before / After Media Template",
    type: "media",
    verticals: [verticalByName("General"), verticalByName("HVAC")],
    categories: [categoryByName("Inspection")],
    source: "library",
    status: "published",
    version: 1,
    fields_count: 4,
    shots_count: 4,
    packs: ["HVAC Starter Pack", "General Service Pack"],
    description: "Required before and after photos.",
    recommended: true,
    created: "2026-02-12T10:00:00Z",
    modified: "2026-03-18T10:00:00Z",
    payload_json: {
      shots: [
        "Before work area",
        "Before equipment",
        "After work area",
        "After equipment",
      ],
    },
  },
  {
    id: 104,
    name: "Customer Sign-off Form",
    type: "form",
    verticals: [verticalByName("General")],
    categories: [categoryByName("Customer Sign-off")],
    source: "library",
    status: "published",
    version: 1,
    fields_count: 7,
    shots_count: null,
    packs: ["General Service Pack", "HVAC Starter Pack"],
    description: "Customer completion approval.",
    recommended: false,
    created: "2026-01-20T10:00:00Z",
    modified: "2026-03-05T10:00:00Z",
    payload_json: {
      sectionsData: [
        {
          name: "Section 1 - Completion",
          fields: [
            { icon: "TXT", name: "Customer name", required: true },
            { icon: "SIGN", name: "Customer signature", required: true },
          ],
        },
      ],
    },
  },
  {
    id: 105,
    name: "Customer Base Model",
    type: "model",
    verticals: [verticalByName("General")],
    categories: [categoryByName("Installation")],
    source: "library",
    status: "published",
    version: 2,
    fields_count: 8,
    shots_count: null,
    packs: ["General Service Pack", "HVAC Starter Pack"],
    description: "Base customer data model.",
    recommended: false,
    created: "2026-01-20T10:00:00Z",
    modified: "2026-03-05T10:00:00Z",
    payload_json: {
      keyFields: ["Customer ID", "Name", "Primary contact", "Status"],
      relations: ["Sites (1:N)", "Jobs (1:N)"],
    },
  },
  {
    id: 106,
    name: "Equipment Model",
    type: "model",
    verticals: [verticalByName("HVAC"), verticalByName("Facility")],
    categories: [categoryByName("Maintenance")],
    source: "library",
    status: "draft",
    version: 1,
    fields_count: 10,
    shots_count: null,
    packs: [],
    description: "Equipment master model.",
    recommended: false,
    created: "2026-03-01T10:00:00Z",
    modified: "2026-03-11T10:00:00Z",
    payload_json: {
      keyFields: ["Manufacturer", "Model", "Serial Number", "Install Date"],
      relations: ["Customer (FK)", "Maintenance Jobs (1:N)"],
    },
  },
  {
    id: 107,
    name: "Security Camera Inspection",
    type: "form",
    verticals: [verticalByName("Security")],
    categories: [categoryByName("Inspection")],
    source: "library",
    status: "published",
    version: 1,
    fields_count: 16,
    shots_count: null,
    packs: ["Security Starter Pack"],
    description: "Camera inspection form.",
    recommended: false,
    created: "2026-03-04T10:00:00Z",
    modified: "2026-03-22T10:00:00Z",
    payload_json: {
      sectionsData: [
        {
          name: "Section 1 - Camera checks",
          fields: [
            { icon: "CHK", name: "Camera powered", required: true },
            { icon: "CAM", name: "Field of view photo", required: true },
          ],
        },
      ],
    },
  },
];

export const mockReviewQueue: ReviewQueueItem[] = [
  {
    id: "rq1",
    source: "Imported Drafts",
    name: "HVAC Emergency Repair Form",
    type: "Form",
    verticals: ["HVAC"],
    category: "Diagnostic",
    status: "Pending Review",
    version: "v1.0",
    fields: 14,
    submittedBy: "Super Admin",
    submittedFrom: "JSON Import",
    description: "Imported JSON draft waiting for global catalog approval.",
  },
  {
    id: "rq2",
    source: "Tenant Submissions",
    name: "Facility Safety Inspection",
    type: "Form",
    verticals: ["Facility", "General"],
    category: "Safety",
    status: "Pending Review",
    version: "v1.0",
    fields: 20,
    submittedBy: "AC Pro Miami",
    submittedFrom: "Master Space",
    description:
      "Submitted from tenant Master Space using Submit to Global Library.",
  },
  {
    id: "rq3",
    source: "Tenant Submissions",
    name: "Roof Unit Photo Checklist",
    type: "Media",
    verticals: ["HVAC"],
    category: "Inspection",
    status: "Changes Requested",
    version: "v1.1",
    fields: 6,
    submittedBy: "AC Pro Miami",
    submittedFrom: "Master Space",
    description: "Media checklist submitted for global reuse.",
    changes: "Clarify category and add Spanish description.",
  },
  {
    id: "rq4",
    source: "Imported Drafts",
    name: "Telecom Site Base Model",
    type: "Model",
    verticals: ["Telecom"],
    category: "Installation",
    status: "Pending Review",
    version: "v1.0",
    fields: 12,
    submittedBy: "Super Admin",
    submittedFrom: "JSON Import",
    description: "Model template imported as draft. Structure preview only.",
  },
];

export const mockTemplatePacks: APILibraryTemplatePackResponseType[] = [
  {
    id: 1001,
    name: "HVAC Starter Pack",
    description: "Start HVAC operations with forms, media and customer model.",
    status: "active",
    verticals: [verticalByName("HVAC"), verticalByName("General")],
    templates: [
      mockTemplates[0],
      mockTemplates[1],
      mockTemplates[2],
      mockTemplates[3],
      mockTemplates[4],
    ],
    created: "2026-02-20T10:00:00Z",
    modified: "2026-04-04T10:00:00Z",
  },
  {
    id: 1002,
    name: "General Service Pack",
    description: "Common templates used by any vertical.",
    status: "active",
    verticals: [verticalByName("General")],
    templates: [mockTemplates[2], mockTemplates[3], mockTemplates[4]],
    created: "2026-02-22T10:00:00Z",
    modified: "2026-03-21T10:00:00Z",
  },
  {
    id: 1003,
    name: "Security Starter Pack",
    description: "Security service starter pack.",
    status: "disabled",
    verticals: [verticalByName("Security"), verticalByName("General")],
    templates: [
      mockTemplates[6],
      mockTemplates[2],
      mockTemplates[3],
      mockTemplates[4],
    ],
    created: "2026-03-10T10:00:00Z",
    modified: "2026-03-19T10:00:00Z",
  },
  {
    id: 1004,
    name: "Facility Starter Pack",
    description: "Facility maintenance starter pack.",
    status: "disabled",
    verticals: [verticalByName("Facility"), verticalByName("General")],
    templates: [
      mockTemplates[1],
      mockTemplates[2],
      mockTemplates[3],
      mockTemplates[4],
    ],
    created: "2026-03-10T10:00:00Z",
    modified: "2026-03-19T10:00:00Z",
  },
];

type MockTemplatePackMetadata = {
  readonly coreVertical: string;
  readonly dependencies: readonly string[];
  readonly plans: readonly string[];
  readonly version: string;
};

const mockTemplatePackMetadataByName: Record<string, MockTemplatePackMetadata> =
  {
    "Facility Starter Pack": {
      coreVertical: "Facility Mgmt",
      dependencies: ["General Service Pack"],
      plans: ["Scale", "Enterprise"],
      version: "v1.0",
    },
    "General Service Pack": {
      coreVertical: "General",
      dependencies: [],
      plans: ["Go", "Growth", "Scale", "Enterprise"],
      version: "v1.0",
    },
    "HVAC Starter Pack": {
      coreVertical: "HVAC",
      dependencies: ["General Service Pack"],
      plans: ["Go", "Growth", "Scale"],
      version: "v1.0",
    },
    "Security Starter Pack": {
      coreVertical: "Security",
      dependencies: ["General Service Pack"],
      plans: ["Growth", "Scale"],
      version: "v1.0",
    },
  };

function getMockTemplatePackMetadata(pack: APILibraryTemplatePackResponseType) {
  return mockTemplatePackMetadataByName[pack.name ?? ""];
}

export function getMockTemplatePackCoreVertical(
  pack: APILibraryTemplatePackResponseType,
) {
  return getMockTemplatePackMetadata(pack)?.coreVertical ?? "";
}

export function getMockTemplatePackDependencies(
  pack: APILibraryTemplatePackResponseType,
) {
  return getMockTemplatePackMetadata(pack)?.dependencies ?? [];
}

export function getMockTemplatePackPlans(
  pack: APILibraryTemplatePackResponseType,
) {
  return getMockTemplatePackMetadata(pack)?.plans ?? [];
}

export function getMockTemplatePackVersion(
  pack: APILibraryTemplatePackResponseType,
) {
  return getMockTemplatePackMetadata(pack)?.version ?? "-";
}

export function isMockTemplatePackAllowedForPlan(
  pack: APILibraryTemplatePackResponseType,
  plan: string,
) {
  return getMockTemplatePackPlans(pack).includes(plan);
}

export const mockAccessPresets: AccessPreset[] = [
  {
    id: "pr1",
    name: "Field Technician",
    role: "Member",
    status: "Published",
    plans: ["Go", "Growth", "Scale"],
    contexts: ["Workspace"],
    capabilities: [
      "dashboard.view.basic",
      "work_orders.view",
      "work_orders.execute",
      "forms.execute",
    ],
  },
  {
    id: "pr2",
    name: "Field Supervisor",
    role: "Supervisor",
    status: "Published",
    plans: ["Growth", "Scale"],
    contexts: ["Workspace"],
    capabilities: [
      "dashboard.view.advanced",
      "work_orders.assign",
      "work_orders.approve",
      "forms.review",
    ],
  },
  {
    id: "pr3",
    name: "Client Viewer",
    role: "Viewer",
    status: "Published",
    plans: ["Scale", "Enterprise"],
    contexts: ["Master Space", "Workspace"],
    capabilities: ["dashboard.view.basic", "forms.view"],
  },
];

export const mockAuditEntries: AuditEntry[] = [];

export function mockRequest<T>(data: T) {
  return new AbortableRequest<T>(
    Promise.resolve({
      data: new SuccessResponse(data),
    } as AxiosResponse<RequestResponse<T>>),
    () => undefined,
  );
}

export function getMockItemById<T extends { id?: number; url?: string }>(
  items: readonly T[],
  id: string | number,
) {
  return items.find(
    (item) =>
      String(item.id ?? "") === String(id) ||
      (item.url?.replace(/\/$/, "").endsWith(`/${id}`) ?? false),
  );
}

export function createMockItem<
  T extends { id?: number; created?: string; modified?: string },
>(items: T[], data: Partial<T> = {}) {
  const now = new Date().toISOString();
  const item = {
    ...data,
    created: now,
    id: getNextMockId(items),
    modified: now,
  } as T;

  items.unshift(item);
  return item;
}

export function updateMockItem<T extends { id?: number; modified?: string }>(
  items: T[],
  id: string | number,
  data: Partial<T>,
) {
  const item = getMockItemById(items, id);
  const next = {
    ...(item ?? {}),
    ...data,
    id: Number(id),
    modified: new Date().toISOString(),
  } as T;

  if (item) {
    Object.assign(item, next);
    return item;
  }

  items.unshift(next);
  return next;
}

export function deleteMockItem<T extends { id?: number; url?: string }>(
  items: T[],
  id: string | number,
) {
  const item = getMockItemById(items, id);
  const index = items.findIndex(
    (candidate) =>
      String(candidate.id ?? "") === String(id) ||
      (candidate.url?.replace(/\/$/, "").endsWith(`/${id}`) ?? false),
  );

  if (index >= 0) {
    items.splice(index, 1);
  }

  return item ?? {};
}

function getNextMockId(items: readonly { id?: number }[]) {
  return Math.max(0, ...items.map((item) => item.id ?? 0)) + 1;
}
